import React, { useEffect, useRef, useState, useCallback } from "react";
import { loadImage } from "@/lib/studioCanvas";
import {
  Eraser, Brush, Lasso, Wand2, Undo2, RotateCcw, Check, X,
  Circle, Square, Triangle, Hexagon, Star, Egg, Scan, Type, Sticker, Trash2, Upload, Plus, Loader2,
} from "lucide-react";

const MAX_DIM = 900;
const HISTORY_LIMIT = 12;

const SHAPES = [
  { id: "circle", label: "Circle", Icon: Circle },
  { id: "ellipse", label: "Ellipse", Icon: Egg },
  { id: "rect", label: "Rectangle", Icon: Square },
  { id: "rounded", label: "Rounded", Icon: Scan },
  { id: "triangle", label: "Triangle", Icon: Triangle },
  { id: "hexagon", label: "Hexagon", Icon: Hexagon },
  { id: "star", label: "Star", Icon: Star },
];

const STICKERS = ["bolt", "reticle", "spark", "orbit", "skull", "chevron"].map((s) => ({
  id: s, src: `/studio/stickers/${s}.png`,
}));

const TEXT_COLORS = ["#00E5FF", "#FFB300", "#FFFFFF", "#0A0F1C", "#FF3B6B", "#7CFF6B"];
const FONTS = [
  { id: "head", label: "Aa", family: "'Chakra Petch', 'Rajdhani', sans-serif" },
  { id: "mono", label: "</>", family: "'JetBrains Mono', monospace" },
];

const uid = () => Math.random().toString(36).slice(2, 9);

// ---- shape path builders ----
function roundRectPath(ctx, x, y, w, h, r) {
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
}
function polyPath(ctx, cx, cy, radius, sides, rot) {
  for (let i = 0; i < sides; i++) {
    const a = rot + (i * 2 * Math.PI) / sides;
    const px = cx + radius * Math.cos(a), py = cy + radius * Math.sin(a);
    i === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py);
  }
}
function starPath(ctx, cx, cy, outer, inner, points, rot) {
  for (let i = 0; i < points * 2; i++) {
    const r = i % 2 === 0 ? outer : inner;
    const a = rot + (i * Math.PI) / points;
    const px = cx + r * Math.cos(a), py = cy + r * Math.sin(a);
    i === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py);
  }
}
function buildShapePath(ctx, kind, w, h) {
  const m = Math.min(w, h) * 0.02;
  const cw = w - m * 2, ch = h - m * 2, cx = w / 2, cy = h / 2;
  switch (kind) {
    case "circle": ctx.arc(cx, cy, Math.min(cw, ch) / 2, 0, Math.PI * 2); break;
    case "ellipse": ctx.ellipse(cx, cy, cw / 2, ch / 2, 0, 0, Math.PI * 2); break;
    case "rect": ctx.rect(m, m, cw, ch); break;
    case "rounded": roundRectPath(ctx, m, m, cw, ch, Math.min(cw, ch) * 0.16); break;
    case "triangle": ctx.moveTo(cx, m); ctx.lineTo(w - m, h - m); ctx.lineTo(m, h - m); break;
    case "hexagon": polyPath(ctx, cx, cy, Math.min(cw, ch) / 2, 6, -Math.PI / 2); break;
    case "star": starPath(ctx, cx, cy, Math.min(cw, ch) / 2, Math.min(cw, ch) / 4.2, 5, -Math.PI / 2); break;
    default: ctx.rect(0, 0, w, h);
  }
}

function autoTrim(src) {
  const { width: w, height: h } = src;
  const ctx = src.getContext("2d", { willReadFrequently: true });
  const { data } = ctx.getImageData(0, 0, w, h);
  let minX = w, minY = h, maxX = 0, maxY = 0, found = false;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (data[(y * w + x) * 4 + 3] > 12) {
        found = true;
        if (x < minX) minX = x; if (x > maxX) maxX = x;
        if (y < minY) minY = y; if (y > maxY) maxY = y;
      }
    }
  }
  if (!found) return src;
  const pad = Math.round(Math.min(w, h) * 0.02);
  minX = Math.max(0, minX - pad); minY = Math.max(0, minY - pad);
  maxX = Math.min(w - 1, maxX + pad); maxY = Math.min(h - 1, maxY + pad);
  const cw = maxX - minX + 1, chh = maxY - minY + 1;
  const out = document.createElement("canvas");
  out.width = cw; out.height = chh;
  out.getContext("2d").drawImage(src, minX, minY, cw, chh, 0, 0, cw, chh);
  return out;
}

export const ImageEditor = ({ open, src, onApply, onClose }) => {
  const canvasRef = useRef(null);
  const baseRef = useRef(null);
  const overlayRef = useRef(null);
  const wrapRef = useRef(null);
  const history = useRef([]);
  const drawing = useRef(false);
  const lastPt = useRef(null);
  const lassoPts = useRef([]);
  const layerDrag = useRef(null);
  const stickerInputRef = useRef(null);

  const [ready, setReady] = useState(false);
  const [tool, setTool] = useState("erase");
  const [brush, setBrush] = useState(48);
  const [lassoMode, setLassoMode] = useState("keep");
  const [canUndo, setCanUndo] = useState(false);
  const [busy, setBusy] = useState(false);

  // optional overlay layers (text + stickers), baked into the artwork on Apply
  const [layers, setLayers] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [textInput, setTextInput] = useState("AETHER");
  const [disp, setDisp] = useState({ w: 0, h: 0 });

  const selected = layers.find((l) => l.id === selectedId);

  useEffect(() => {
    if (!open || !src) return;
    let cancelled = false;
    setReady(false); history.current = []; setCanUndo(false);
    setLayers([]); setSelectedId(null);
    (async () => {
      const img = await loadImage(src);
      if (cancelled || !canvasRef.current) return;
      const scale = Math.min(1, MAX_DIM / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale), h = Math.round(img.height * scale);
      if (!baseRef.current) baseRef.current = document.createElement("canvas");
      const canvas = canvasRef.current, base = baseRef.current, ov = overlayRef.current;
      [canvas, base, ov].filter(Boolean).forEach((c) => { c.width = w; c.height = h; });
      base.getContext("2d").drawImage(img, 0, 0, w, h);
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      ctx.clearRect(0, 0, w, h);
      ctx.drawImage(img, 0, 0, w, h);
      setReady(true);
    })();
    return () => { cancelled = true; };
  }, [open, src]);

  // track displayed canvas size so text layers preview at the right scale
  useEffect(() => {
    if (!open) return;
    const el = canvasRef.current;
    if (!el) return;
    const update = () => setDisp({ w: el.clientWidth, h: el.clientHeight });
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [open, ready]);

  const pushHistory = useCallback(() => {
    const c = canvasRef.current;
    history.current.push(c.getContext("2d").getImageData(0, 0, c.width, c.height));
    if (history.current.length > HISTORY_LIMIT) history.current.shift();
    setCanUndo(true);
  }, []);

  const undo = () => {
    if (!history.current.length) return;
    canvasRef.current.getContext("2d").putImageData(history.current.pop(), 0, 0);
    setCanUndo(history.current.length > 0);
  };

  const reset = () => {
    pushHistory();
    const c = canvasRef.current, ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.drawImage(baseRef.current, 0, 0);
  };

  const toCanvas = (e) => {
    const c = canvasRef.current, r = c.getBoundingClientRect();
    return { x: ((e.clientX - r.left) / r.width) * c.width, y: ((e.clientY - r.top) / r.height) * c.height };
  };

  const paintSegment = (from, to) => {
    const ctx = canvasRef.current.getContext("2d");
    if (tool === "erase") {
      ctx.save();
      ctx.globalCompositeOperation = "destination-out";
      ctx.strokeStyle = "rgba(0,0,0,1)";
      ctx.lineWidth = brush; ctx.lineCap = "round"; ctx.lineJoin = "round";
      ctx.beginPath(); ctx.moveTo(from.x, from.y); ctx.lineTo(to.x, to.y); ctx.stroke();
      ctx.restore();
    } else if (tool === "restore") {
      const steps = Math.max(1, Math.hypot(to.x - from.x, to.y - from.y) / (brush / 3));
      for (let i = 0; i <= steps; i++) {
        const x = from.x + (to.x - from.x) * (i / steps), y = from.y + (to.y - from.y) * (i / steps);
        ctx.save();
        ctx.beginPath(); ctx.arc(x, y, brush / 2, 0, Math.PI * 2); ctx.clip();
        ctx.drawImage(baseRef.current, 0, 0);
        ctx.restore();
      }
    }
  };

  const drawOverlayRing = (pt) => {
    const ov = overlayRef.current, octx = ov.getContext("2d");
    octx.clearRect(0, 0, ov.width, ov.height);
    if (pt && (tool === "erase" || tool === "restore")) {
      octx.beginPath(); octx.arc(pt.x, pt.y, brush / 2, 0, Math.PI * 2);
      octx.strokeStyle = "rgba(0,229,255,0.9)"; octx.lineWidth = 2; octx.stroke();
    }
  };

  const drawLassoGuide = () => {
    const ov = overlayRef.current, octx = ov.getContext("2d");
    octx.clearRect(0, 0, ov.width, ov.height);
    const pts = lassoPts.current;
    if (pts.length < 1) return;
    octx.beginPath(); octx.moveTo(pts[0].x, pts[0].y);
    pts.forEach((p) => octx.lineTo(p.x, p.y));
    octx.strokeStyle = "#00E5FF"; octx.lineWidth = 2; octx.setLineDash([7, 5]); octx.stroke();
    octx.setLineDash([]);
  };

  const applyLasso = () => {
    const pts = lassoPts.current;
    overlayRef.current.getContext("2d").clearRect(0, 0, overlayRef.current.width, overlayRef.current.height);
    if (pts.length < 3) { lassoPts.current = []; return; }
    pushHistory();
    const c = canvasRef.current, ctx = c.getContext("2d");
    if (lassoMode === "remove") {
      ctx.save(); ctx.globalCompositeOperation = "destination-out";
      ctx.beginPath(); ctx.moveTo(pts[0].x, pts[0].y);
      pts.forEach((p) => ctx.lineTo(p.x, p.y)); ctx.closePath(); ctx.fill(); ctx.restore();
    } else {
      const tmp = document.createElement("canvas");
      tmp.width = c.width; tmp.height = c.height;
      const tctx = tmp.getContext("2d");
      tctx.save(); tctx.beginPath(); tctx.moveTo(pts[0].x, pts[0].y);
      pts.forEach((p) => tctx.lineTo(p.x, p.y)); tctx.closePath(); tctx.clip();
      tctx.drawImage(c, 0, 0); tctx.restore();
      ctx.clearRect(0, 0, c.width, c.height); ctx.drawImage(tmp, 0, 0);
    }
    lassoPts.current = [];
  };

  const onDown = (e) => {
    if (!ready) return;
    setSelectedId(null);
    e.currentTarget.setPointerCapture?.(e.pointerId);
    const pt = toCanvas(e);
    drawing.current = true;
    if (tool === "lasso") { lassoPts.current = [pt]; drawLassoGuide(); return; }
    pushHistory(); lastPt.current = pt; paintSegment(pt, pt);
  };
  const onMove = (e) => {
    if (!ready) return;
    const pt = toCanvas(e);
    if (!drawing.current) { drawOverlayRing(pt); return; }
    if (tool === "lasso") { lassoPts.current.push(pt); drawLassoGuide(); return; }
    paintSegment(lastPt.current, pt); lastPt.current = pt; drawOverlayRing(pt);
  };
  const onUp = () => {
    if (!drawing.current) return;
    drawing.current = false;
    if (tool === "lasso") applyLasso();
    overlayRef.current.getContext("2d").clearRect(0, 0, overlayRef.current.width, overlayRef.current.height);
  };

  const applyShape = (kind) => {
    pushHistory();
    const c = canvasRef.current, ctx = c.getContext("2d");
    const tmp = document.createElement("canvas");
    tmp.width = c.width; tmp.height = c.height;
    const tctx = tmp.getContext("2d");
    tctx.save(); tctx.beginPath(); buildShapePath(tctx, kind, c.width, c.height);
    tctx.closePath(); tctx.clip(); tctx.drawImage(c, 0, 0); tctx.restore();
    ctx.clearRect(0, 0, c.width, c.height); ctx.drawImage(tmp, 0, 0);
  };

  const magicRemove = () => {
    pushHistory();
    const c = canvasRef.current, ctx = c.getContext("2d");
    const { width: w, height: h } = c;
    const img = ctx.getImageData(0, 0, w, h), d = img.data;
    const corners = [0, (w - 1) * 4, (h - 1) * w * 4, ((h - 1) * w + (w - 1)) * 4];
    let r = 0, g = 0, b = 0;
    corners.forEach((i) => { r += d[i]; g += d[i + 1]; b += d[i + 2]; });
    r /= 4; g /= 4; b /= 4;
    const tol = 60;
    for (let i = 0; i < d.length; i += 4) {
      if (Math.sqrt((d[i] - r) ** 2 + (d[i + 1] - g) ** 2 + (d[i + 2] - b) ** 2) < tol) d[i + 3] = 0;
    }
    ctx.putImageData(img, 0, 0);
  };

  // ---- optional layers (text + stickers) ----
  const addText = () => {
    const id = uid();
    setLayers((ls) => [...ls, { id, type: "text", text: textInput || "TEXT", color: "#00E5FF", sizeFrac: 0.12, x: 0.5, y: 0.5, rotation: 0, font: FONTS[0].family }]);
    setSelectedId(id);
  };
  const addSticker = (srcUrl) => {
    const id = uid();
    setLayers((ls) => [...ls, { id, type: "sticker", src: srcUrl, scaleFrac: 0.32, x: 0.5, y: 0.5, rotation: 0 }]);
    setSelectedId(id);
  };
  const onStickerUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fr = new FileReader();
    fr.onload = () => addSticker(fr.result);
    fr.readAsDataURL(file);
    e.target.value = "";
  };
  const updateSelected = (patch) => setLayers((ls) => ls.map((l) => (l.id === selectedId ? { ...l, ...patch } : l)));
  const deleteSelected = () => { setLayers((ls) => ls.filter((l) => l.id !== selectedId)); setSelectedId(null); };

  const startLayerDrag = (e, id) => {
    e.stopPropagation();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setSelectedId(id);
    layerDrag.current = { id };
  };
  const moveLayer = (e) => {
    if (!layerDrag.current || !wrapRef.current) return;
    const r = wrapRef.current.getBoundingClientRect();
    const x = Math.min(1, Math.max(0, (e.clientX - r.left) / r.width));
    const y = Math.min(1, Math.max(0, (e.clientY - r.top) / r.height));
    setLayers((ls) => ls.map((l) => (l.id === layerDrag.current.id ? { ...l, x, y } : l)));
  };
  const endLayerDrag = () => { layerDrag.current = null; };

  const handleApply = async () => {
    if (!ready) return;
    setBusy(true);
    try {
      // preload all sticker images in parallel before baking (no awaits in the draw loop)
      const stickerImgs = {};
      await Promise.all(
        layers.filter((l) => l.type === "sticker").map(async (l) => { stickerImgs[l.id] = await loadImage(l.src); })
      );
      const c = canvasRef.current;
      const out = document.createElement("canvas");
      out.width = c.width; out.height = c.height;
      const o = out.getContext("2d");
      o.drawImage(c, 0, 0);
      for (const l of layers) {
        o.save();
        o.translate(l.x * c.width, l.y * c.height);
        o.rotate((l.rotation * Math.PI) / 180);
        if (l.type === "text") {
          const fs = l.sizeFrac * c.height;
          o.font = `bold ${fs}px ${l.font}`;
          o.fillStyle = l.color; o.textAlign = "center"; o.textBaseline = "middle";
          o.fillText(l.text, 0, 0);
        } else {
          const img = stickerImgs[l.id];
          if (img) {
            const w = l.scaleFrac * c.width, h = w * (img.height / img.width);
            o.drawImage(img, -w / 2, -h / 2, w, h);
          }
        }
        o.restore();
      }
      onApply(autoTrim(out).toDataURL("image/png"));
    } finally { setBusy(false); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-app/90 backdrop-blur-sm p-3 sm:p-5" data-testid="image-editor">
      <div className="relative w-full max-w-5xl max-h-[94vh] overflow-y-auto glass-strong clip-chamfer brackets p-4 sm:p-6">
        <button onClick={onClose} data-testid="editor-close" className="absolute top-3 right-3 text-slate-400 hover:text-cyan z-10">
          <X className="w-5 h-5" />
        </button>
        <div className="font-mono text-[10px] tracking-[0.3em] text-cyan/70 mb-4">IMAGE EDITOR · MASK · CUT-OUT · TEXT · STICKERS</div>

        <div className="grid lg:grid-cols-12 gap-5">
          {/* canvas + layers */}
          <div className="lg:col-span-8 order-2 lg:order-1">
            <div className="flex items-center justify-center">
              <div ref={wrapRef} className="relative inline-block" data-testid="editor-stage"
                style={{ lineHeight: 0, backgroundImage: "linear-gradient(45deg,#11182a 25%,transparent 25%),linear-gradient(-45deg,#11182a 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#11182a 75%),linear-gradient(-45deg,transparent 75%,#11182a 75%)", backgroundSize: "20px 20px", backgroundPosition: "0 0,0 10px,10px -10px,-10px 0px", backgroundColor: "#0a0f1c" }}>
                <canvas ref={canvasRef} data-testid="editor-canvas"
                  className="block max-w-full" style={{ maxHeight: "58vh", width: "auto", height: "auto", touchAction: "none" }}
                  onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerLeave={onUp} />
                <canvas ref={overlayRef} className="absolute inset-0 w-full h-full pointer-events-none" />

                {/* draggable optional layers */}
                {layers.map((l) => (
                  <div key={l.id} data-testid={`layer-${l.id}`}
                    onPointerDown={(e) => startLayerDrag(e, l.id)}
                    onPointerMove={moveLayer} onPointerUp={endLayerDrag}
                    className={`absolute select-none ${selectedId === l.id ? "outline outline-1 outline-cyan/80" : ""}`}
                    style={{
                      left: `${l.x * 100}%`, top: `${l.y * 100}%`,
                      transform: `translate(-50%,-50%) rotate(${l.rotation}deg)`,
                      cursor: "move", touchAction: "none",
                      ...(l.type === "sticker" ? { width: `${l.scaleFrac * 100}%` } : {}),
                    }}>
                    {l.type === "text" ? (
                      <span style={{ color: l.color, fontFamily: l.font, fontWeight: 700, fontSize: `${Math.max(8, l.sizeFrac * disp.h)}px`, whiteSpace: "nowrap", lineHeight: 1, textShadow: "0 0 6px rgba(0,0,0,0.35)" }}>
                        {l.text}
                      </span>
                    ) : (
                      <img src={l.src} alt="sticker" draggable={false} className="w-full h-auto pointer-events-none" />
                    )}
                  </div>
                ))}

                {!ready && (
                  <div className="absolute inset-0 flex items-center justify-center font-mono text-[10px] tracking-widest text-cyan/60">LOADING…</div>
                )}
              </div>
            </div>
            <div className="font-mono text-[9px] text-slate-500 text-center mt-2">
              {selected ? "Drag the selected layer on the artwork · use the controls to size & rotate"
                : tool === "lasso" ? "Draw an outline with your finger / mouse — release to apply"
                : "Drag over the image to " + (tool === "erase" ? "erase" : "restore")}
            </div>
          </div>

          {/* tools */}
          <div className="lg:col-span-4 order-1 lg:order-2 space-y-4">
            <div>
              <div className="font-mono text-[9px] tracking-widest text-slate-400 mb-2">TOOL</div>
              <div className="grid grid-cols-3 gap-2">
                <ToolBtn active={tool === "erase"} onClick={() => setTool("erase")} Icon={Eraser} label="Erase" testid="tool-erase" />
                <ToolBtn active={tool === "restore"} onClick={() => setTool("restore")} Icon={Brush} label="Restore" testid="tool-restore" />
                <ToolBtn active={tool === "lasso"} onClick={() => setTool("lasso")} Icon={Lasso} label="Lasso" testid="tool-lasso" />
              </div>
            </div>

            {(tool === "erase" || tool === "restore") && (
              <label className="block" data-testid="brush-control">
                <span className="font-mono text-[9px] tracking-widest text-slate-400">BRUSH · {brush}px</span>
                <input type="range" min="8" max="160" step="2" value={brush} data-testid="brush-slider"
                  onChange={(e) => setBrush(parseInt(e.target.value))} className="w-full accent-cyan mt-1" />
              </label>
            )}

            {tool === "lasso" && (
              <div className="grid grid-cols-2 gap-2" data-testid="lasso-mode">
                {["keep", "remove"].map((m) => (
                  <button key={m} onClick={() => setLassoMode(m)} data-testid={`lasso-${m}`}
                    className={`font-mono text-[9px] tracking-widest py-2 border transition-all ${lassoMode === m ? "border-cyan text-cyan box-glow-cyan" : "border-white/15 text-slate-400"}`}>
                    {m === "keep" ? "KEEP INSIDE" : "REMOVE INSIDE"}
                  </button>
                ))}
              </div>
            )}

            <div>
              <div className="font-mono text-[9px] tracking-widest text-slate-400 mb-2">SHAPE FRAME</div>
              <div className="grid grid-cols-4 gap-2" data-testid="shape-frames">
                {SHAPES.map((s) => (
                  <button key={s.id} onClick={() => applyShape(s.id)} title={s.label} data-testid={`shape-${s.id}`}
                    className="flex flex-col items-center gap-1 p-2 border border-white/10 hover:border-cyan text-slate-300 hover:text-cyan transition-colors">
                    <s.Icon className="w-4 h-4" /><span className="font-mono text-[7px] tracking-wide">{s.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <button onClick={magicRemove} data-testid="magic-remove"
              className="btn-hud w-full flex items-center justify-center gap-2 border border-amber/40 text-amber py-2.5 text-xs hover:bg-amber hover:text-app">
              <Wand2 className="w-4 h-4" /> Magic Background Remove
            </button>

            {/* TEXT (optional) */}
            <div className="border-t border-white/10 pt-4">
              <div className="flex items-center gap-2 font-mono text-[9px] tracking-widest text-cyan/70 mb-2"><Type className="w-3.5 h-3.5" /> ADD TEXT <span className="text-slate-500">(optional)</span></div>
              <div className="flex gap-2">
                <input value={textInput} onChange={(e) => setTextInput(e.target.value)} data-testid="text-input" placeholder="Your text"
                  className="flex-1 bg-cyan/5 border border-cyan/25 focus:border-cyan text-slate-100 px-2 py-1.5 font-mono text-xs outline-none" />
                <button onClick={addText} data-testid="add-text-btn" className="px-3 border border-cyan text-cyan hover:bg-cyan hover:text-app transition-colors"><Plus className="w-4 h-4" /></button>
              </div>
            </div>

            {/* STICKERS (optional) */}
            <div>
              <div className="flex items-center gap-2 font-mono text-[9px] tracking-widest text-cyan/70 mb-2"><Sticker className="w-3.5 h-3.5" /> STICKERS <span className="text-slate-500">(optional)</span></div>
              <div className="grid grid-cols-4 gap-2" data-testid="sticker-tray">
                {STICKERS.map((s) => (
                  <button key={s.id} onClick={() => addSticker(s.src)} data-testid={`sticker-${s.id}`}
                    className="aspect-square border border-white/10 hover:border-cyan p-1 bg-app/40 transition-colors">
                    <img src={s.src} alt={s.id} className="w-full h-full object-contain" />
                  </button>
                ))}
                <button onClick={() => stickerInputRef.current?.click()} data-testid="sticker-upload-btn"
                  className="aspect-square border border-dashed border-cyan/40 hover:border-cyan text-cyan flex items-center justify-center transition-colors">
                  <Upload className="w-4 h-4" />
                </button>
              </div>
              <input ref={stickerInputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={onStickerUpload} data-testid="sticker-file-input" />
            </div>

            {/* SELECTED LAYER controls */}
            {selected && (
              <div className="border border-cyan/30 p-3 space-y-3" data-testid="layer-controls">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[9px] tracking-widest text-cyan/80">{selected.type === "text" ? "TEXT LAYER" : "STICKER LAYER"}</span>
                  <button onClick={deleteSelected} data-testid="layer-delete" className="text-rose-400 hover:text-rose-300"><Trash2 className="w-4 h-4" /></button>
                </div>
                {selected.type === "text" && (
                  <>
                    <input value={selected.text} onChange={(e) => updateSelected({ text: e.target.value })} data-testid="layer-text-edit"
                      className="w-full bg-cyan/5 border border-cyan/25 focus:border-cyan text-slate-100 px-2 py-1.5 font-mono text-xs outline-none" />
                    <div className="flex flex-wrap gap-1.5" data-testid="text-colors">
                      {TEXT_COLORS.map((c) => (
                        <button key={c} onClick={() => updateSelected({ color: c })} title={c}
                          className={`w-6 h-6 rounded-full border-2 ${selected.color === c ? "border-cyan scale-110" : "border-white/20"}`} style={{ background: c }} />
                      ))}
                    </div>
                    <div className="flex gap-1.5">
                      {FONTS.map((f) => (
                        <button key={f.id} onClick={() => updateSelected({ font: f.family })}
                          className={`px-3 py-1 border font-mono text-xs ${selected.font === f.family ? "border-cyan text-cyan" : "border-white/15 text-slate-400"}`}
                          style={{ fontFamily: f.family }}>{f.label}</button>
                      ))}
                    </div>
                  </>
                )}
                <label className="block">
                  <span className="font-mono text-[8px] tracking-widest text-slate-400">SIZE</span>
                  {selected.type === "text" ? (
                    <input type="range" min="0.04" max="0.4" step="0.005" value={selected.sizeFrac} data-testid="layer-size"
                      onChange={(e) => updateSelected({ sizeFrac: parseFloat(e.target.value) })} className="w-full accent-cyan" />
                  ) : (
                    <input type="range" min="0.08" max="0.9" step="0.01" value={selected.scaleFrac} data-testid="layer-size"
                      onChange={(e) => updateSelected({ scaleFrac: parseFloat(e.target.value) })} className="w-full accent-cyan" />
                  )}
                </label>
                <label className="block">
                  <span className="font-mono text-[8px] tracking-widest text-slate-400">ROTATE</span>
                  <input type="range" min="-180" max="180" step="1" value={selected.rotation} data-testid="layer-rotate"
                    onChange={(e) => updateSelected({ rotation: parseInt(e.target.value) })} className="w-full accent-cyan" />
                </label>
              </div>
            )}

            <div className="grid grid-cols-2 gap-2">
              <button onClick={undo} disabled={!canUndo} data-testid="editor-undo"
                className="btn-hud flex items-center justify-center gap-2 border border-white/15 text-slate-300 py-2.5 text-xs hover:border-cyan hover:text-cyan disabled:opacity-40">
                <Undo2 className="w-3.5 h-3.5" /> Undo
              </button>
              <button onClick={reset} data-testid="editor-reset"
                className="btn-hud flex items-center justify-center gap-2 border border-white/15 text-slate-300 py-2.5 text-xs hover:border-cyan hover:text-cyan">
                <RotateCcw className="w-3.5 h-3.5" /> Reset
              </button>
            </div>

            <button onClick={handleApply} disabled={!ready || busy} data-testid="editor-apply"
              className="btn-hud w-full flex items-center justify-center gap-2 border border-cyan text-cyan py-3 text-sm box-glow-cyan hover:bg-cyan hover:text-app disabled:opacity-40">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} {busy ? "Applying…" : "Apply to Garment"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ToolBtn = ({ active, onClick, Icon, label, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`flex flex-col items-center gap-1 p-2.5 border transition-all ${active ? "border-cyan text-cyan box-glow-cyan" : "border-white/10 text-slate-400 hover:border-cyan/40"}`}>
    <Icon className="w-4 h-4" /><span className="font-mono text-[8px] tracking-widest">{label}</span>
  </button>
);
